import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaUser, FaSignOutAlt, FaBars, FaTimes } from "react-icons/fa";
import { useQuery } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";
import "./Navbar.css";
import {
  isAuthenticated,
  getCurrentUser,
  clearAuthData,
  isAdmin,
} from "../utils/auth";
import api from "../services/api";
import type { FeedbackStatsResponse } from "../types/feedback.types";
import UserAvatarMenu from "./UserAvatarMenu";

interface SystemConfigResponse {
  data?: {
    value?: string;
  };
}

const Navbar: React.FC = () => {
  const { t } = useTranslation("common");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isAuth = isAuthenticated();
  const user = getCurrentUser() || { username: "", role: "" };

  // Fetch feedback stats for admin badge
  const { data: feedbackStatsData } = useQuery<FeedbackStatsResponse>(
    ["feedback-stats"],
    () => api.feedback.getFeedbackStats(),
    {
      enabled: isAuth && isAdmin(),
      staleTime: 60 * 1000,
      refetchInterval: 60 * 1000,
      onError: (error) => {
        console.warn("Failed to fetch feedback stats for badge:", error);
      },
    },
  );

  // Fetch agency mode setting
  const { data: agencyModeData } = useQuery<SystemConfigResponse>(
    ["system-config", "agency_mode"],
    () =>
      recruitingAPI.getSystemConfig(
        "agency_mode",
      ) as Promise<SystemConfigResponse>,
    {
      enabled: isAuth && isAdmin(),
      staleTime: 5 * 60 * 1000,
      retry: 1,
      retryDelay: 1000,
      onError: (error) => {
        console.error("Error fetching agency mode:", error);
      },
    },
  );

  // Hide navbar for unauthenticated users and onboarding pages
  if (!isAuth || location.pathname.startsWith("/onboarding")) {
    return null;
  }

  const isAgencyMode = agencyModeData?.data?.value === "true";
  const isTechChecker = user.role === "tech_checker";
  const newFeedbackCount = feedbackStatsData?.data?.newCount ?? 0;

  const handleLogout = async () => {
    try {
      await api.auth.logout();
    } catch {
      // Logout API failure is non-critical — user is leaving anyway
    }
    clearAuthData();
    navigate("/login");
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Hide navbar for public pages
  const isPublicPage = location.pathname.startsWith("/candidate/");

  if (!isAuth || isPublicPage) {
    return null;
  }

  return (
    <>
      {isMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={closeMenu}
          onKeyDown={(e) => e.key === "Escape" && closeMenu()}
          aria-hidden="true"
        />
      )}
      <nav className="navbar">
        <div className="navbar-container">
          <Link to="/" className="navbar-brand" onClick={closeMenu}>
            <div className="brand-container">
              <img
                src="/hirefocuz-logo.png"
                alt="HireFocuZ"
                className="recruitmentmaster-logo"
              />
            </div>
          </Link>

          <div className={`navbar-menu ${isMenuOpen ? "active" : ""}`}>
            {isTechChecker ? (
              <>
                <Link
                  to="/candidates"
                  className={`navbar-link ${
                    location.pathname === "/candidates" ||
                    location.pathname.startsWith("/candidates/")
                      ? "active"
                      : ""
                  }`}
                  onClick={closeMenu}
                >
                  {t("navigation.candidates")}
                </Link>
                <Link
                  to="/profile"
                  className={`navbar-link ${
                    location.pathname === "/profile" ? "active" : ""
                  }`}
                  onClick={closeMenu}
                >
                  <FaUser /> {t("navigation.profile")}
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/dashboard"
                  className={`navbar-link ${
                    location.pathname === "/dashboard" ? "active" : ""
                  }`}
                  onClick={closeMenu}
                >
                  {t("navigation.dashboard")}
                </Link>
                <Link
                  to="/candidates"
                  className={`navbar-link ${
                    location.pathname === "/candidates" ||
                    location.pathname.startsWith("/candidates/")
                      ? "active"
                      : ""
                  }`}
                  onClick={closeMenu}
                >
                  {t("navigation.candidates")}
                </Link>
                <Link
                  to="/jobs"
                  className={`navbar-link ${
                    location.pathname.startsWith("/jobs") ? "active" : ""
                  }`}
                  onClick={closeMenu}
                >
                  {t("navigation.jobs")}
                </Link>
                {isAgencyMode && (
                  <Link
                    to="/customers"
                    className={`navbar-link ${
                      location.pathname.startsWith("/customers") ? "active" : ""
                    }`}
                    onClick={closeMenu}
                  >
                    {t("navigation.customers")}
                  </Link>
                )}
                <Link
                  to="/pipeline"
                  className={`navbar-link ${
                    location.pathname === "/pipeline" ? "active" : ""
                  }`}
                  onClick={closeMenu}
                >
                  {t("navigation.applications")}
                </Link>
                <Link
                  to="/wiki"
                  className={`navbar-link ${
                    location.pathname === "/wiki" ? "active" : ""
                  }`}
                  onClick={closeMenu}
                >
                  {t("navigation.wiki")}
                </Link>
                <Link
                  to="/analytics"
                  className={`navbar-link ${
                    location.pathname === "/analytics" ? "active" : ""
                  }`}
                  onClick={closeMenu}
                >
                  {t("navigation.analytics")}
                </Link>

                {isAdmin() && (
                  <Link
                    to="/admin"
                    className={`navbar-link mobile-only-link ${
                      location.pathname === "/admin" ? "active" : ""
                    }`}
                    onClick={closeMenu}
                    style={{ position: "relative" }}
                  >
                    {t("navigation.admin")}
                    {newFeedbackCount > 0 && (
                      <span className="feedback-badge">{newFeedbackCount}</span>
                    )}
                  </Link>
                )}

                <Link
                  to="/profile"
                  className={`navbar-link mobile-only-link ${
                    location.pathname === "/profile" ? "active" : ""
                  }`}
                  onClick={closeMenu}
                >
                  <FaUser /> {t("navigation.profile")}
                </Link>

                {/* logout - mobile only (desktop uses avatar menu) */}
                {isMenuOpen && (
                  <button onClick={handleLogout}>
                    <FaSignOutAlt /> {t("navigation.logout")}
                  </button>
                )}
              </>
            )}
          </div>

          <div className="navbar-actions">
            <UserAvatarMenu
              user={user}
              onLogout={handleLogout}
              newFeedbackCount={newFeedbackCount}
            />
          </div>

          <button className="navbar-toggle" onClick={toggleMenu}>
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
